import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Terminal, MessageCircle } from 'lucide-react';

const faqCategories = [
  {
    id: 'pos',
    code: 'CAT-01',
    title: 'Ponto de Venda & Vendas',
    questions: [
      {
        q: 'O MPV funciona como um ponto de venda completo?',
        a: 'Sim. O Meu Ponto de Venda (MPV) permite registar vendas, emitir faturas, recibos e VD, gerir catálogo de produtos e serviços, e acompanhar receitas em tempo real.'
      },
      {
        q: 'Posso ter vários utilizadores a operar em simultâneo?',
        a: 'Sim. O sistema suporta múltiplos utilizadores simultâneos com permissões e cargos configuráveis (Administrador, Supervisor, Vendedor, etc.).'
      },
      {
        q: 'O sistema controla stock automaticamente?',
        a: 'Sim. Cada venda registada atualiza o stock em tempo real, com histórico de movimentações e alertas de nível mínimo.'
      },
      {
        q: 'Posso vender produtos físicos e serviços?',
        a: 'Sim. É possível registar produtos com controlo de inventário, serviços sem stock, e combos/subscrições na mesma plataforma.'
      },
    ]
  },
  {
    id: 'planos',
    code: 'CAT-02',
    title: 'Planos & Licenciamento',
    questions: [
      {
        q: 'Como funciona o Founders Lifetime Pass?',
        a: 'É uma licença vitalícia por pagamento único: Starter (39.900 MT), Professional (79.900 MT) e Enterprise (129.900 MT). Paga uma única vez e utiliza o MPV para sempre sem qualquer custo de mensalidade.'
      },
      {
        q: 'Qual é a diferença entre os três planos?',
        a: 'O Starter (39.900 MT) cobre o essencial (POS, catálogo online, stock e documentos). O Professional (79.900 MT) adiciona pipeline comercial CRM, propostas, metas, comissões e Unit Economics. O Enterprise (129.900 MT) inclui gestão multi-loja, módulos verticais e gestor dedicado.'
      },
      {
        q: 'A configuração inicial tem custo extra?',
        a: 'Não. A equipa do MPV auxilia na configuração inicial dos produtos, utilizadores e menus sem qualquer taxa de setup.'
      },
      {
        q: 'Tenho direito a suporte e atualizações?',
        a: 'Sim. O acesso vitalício inclui suporte contínuo via WhatsApp e todas as atualizações da plataforma.'
      },
    ]
  },
  {
    id: 'portal',
    code: 'CAT-03',
    title: 'Portal Público & E-commerce',
    questions: [
      {
        q: 'O MPV inclui uma loja online própria?',
        a: 'Sim. O Portal Público permite expor o seu catálogo de produtos/serviços na web e receber pedidos de clientes 24 horas por dia.'
      },
      {
        q: 'O website está integrado com o POS e stock?',
        a: 'Sim. Todos os produtos, preços e stocks do seu ponto de venda físico sincronizam automaticamente com a sua loja online.'
      },
      {
        q: 'Posso personalizar o portal com o meu logótipo e cores?',
        a: 'Sim. Pode personalizar logótipo, cores da marca, textos de apresentação, contactos e canais de atendimento.'
      },
    ]
  },
  {
    id: 'financeiro',
    code: 'CAT-04',
    title: 'Financeiro & Pagamentos',
    questions: [
      {
        q: 'Quais métodos de pagamento são suportados?',
        a: 'O sistema suporta pagamentos móveis M-Pesa e e-Mola, numerário, TPA/cartão, transferências bancárias e pagamentos mistos.'
      },
      {
        q: 'Que relatórios financeiros estão disponíveis?',
        a: 'Relatórios de faturação por período, margem de contribuição por produto, curva ABC de vendas, rentabilidade por cliente e acompanhamento de metas.'
      },
      {
        q: 'O que é a análise de Unit Economics?',
        a: 'É a visualização da rentabilidade real por venda e por cliente, permitindo identificar onde está o lucro e eliminar desperdícios operacionais.'
      },
    ]
  },
  {
    id: 'industrias',
    code: 'CAT-05',
    title: 'Módulos por Indústria',
    questions: [
      {
        q: 'Existe módulo para ginásios e academias?',
        a: 'Sim. O módulo de Membresias & Presenças inclui gestão de subscrições de alunos, controlo de presenças e alertas automáticos de expiração.'
      },
      {
        q: 'Existe módulo para microcrédito e crédito?',
        a: 'Sim. O módulo de Empréstimos permite simulação de parcelas, emissão de contratos e gestão de risco visual por cores conforme o estado de pagamento.'
      },
      {
        q: 'Existe suporte para restauração e cozinhas?',
        a: 'Sim. Inclui ecrã de cozinha (KDS) em tempo real, pedidos de mesas e confirmação rápida de pedidos.'
      },
    ]
  },
  {
    id: 'seguranca',
    code: 'CAT-06',
    title: 'Segurança & Suporte',
    questions: [
      {
        q: 'Os meus dados comerciais estão seguros?',
        a: 'Sim. A plataforma utiliza autenticação segura JWT, encriptação HTTPS e infraestrutura com cópias de segurança automáticas.'
      },
      {
        q: 'Preciso de instalar algum software ou servidor local?',
        a: 'Não. O MPV funciona 100% no navegador em qualquer computador, tablet ou smartphone com ligação à internet.'
      },
      {
        q: 'Como funciona o suporte técnico?',
        a: 'O suporte está disponível via WhatsApp e email em dias úteis para esclarecimento de dúvidas e apoio operacional.'
      },
    ]
  },
];

const AccordionItem = ({
  item,
  isOpen,
  onClick
}: {
  item: { q: string; a: string };
  isOpen: boolean;
  onClick: () => void;
}) => (
  <div
    className={`border transition-all duration-300 ${
      isOpen
        ? 'border-[#52C47C]/40 bg-[#52C47C]/5'
        : 'border-white/10 bg-white/[0.02] hover:border-white/20'
    }`}
  >
    <button
      onClick={onClick}
      className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 focus:outline-none"
    >
      <span className={`font-light text-sm leading-snug transition-colors ${
        isOpen ? 'text-white font-normal' : 'text-slate-300 hover:text-white'
      }`}>
        {item.q}
      </span>
      <div className={`shrink-0 w-7 h-7 border flex items-center justify-center transition-all duration-300 ${
        isOpen
          ? 'border-[#52C47C] text-[#52C47C] rotate-180 bg-[#52C47C]/10'
          : 'border-white/20 text-slate-500'
      }`}>
        <ChevronDown size={14} />
      </div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="px-6 pb-6 pt-0">
            <div className="h-px w-full bg-white/10 mb-4" />
            <p className="text-slate-400 text-sm font-light leading-relaxed">{item.a}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const FAQSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState('pos');
  const [openQuestion, setOpenQuestion] = useState<number | null>(0);

  const activeCategory = faqCategories.find(cat => cat.id === activeTab);

  const toggleQuestion = (index: number) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
      {/* Categorias laterais */}
      <div className="w-full lg:w-72 shrink-0 flex flex-col gap-2">
        {faqCategories.map((category) => (
          <div key={category.id}>
            <button
              onClick={() => {
                setActiveTab(category.id);
                setOpenQuestion(0);
              }}
              className={`w-full text-left px-5 py-3.5 font-mono text-xs tracking-wider uppercase transition-all flex items-center justify-between border ${
                activeTab === category.id
                  ? 'bg-[#52C47C]/10 border-[#52C47C]/50 text-[#52C47C] font-bold'
                  : 'bg-white/[0.02] border-white/10 text-slate-400 hover:border-white/20 hover:text-white'
              }`}
            >
              <div>
                <span className="text-[9px] text-slate-600 block mb-0.5">{category.code}</span>
                {category.title}
              </div>
              {activeTab === category.id && (
                <div className="w-1.5 h-1.5 bg-[#52C47C] rounded-full shrink-0" />
              )}
            </button>

            {/* Mobile accordion inline */}
            <div className="lg:hidden">
              <AnimatePresence>
                {activeTab === category.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden space-y-2 pt-2 pb-4"
                  >
                    {category.questions.map((item, index) => (
                      <AccordionItem
                        key={index}
                        item={item}
                        isOpen={openQuestion === index}
                        onClick={() => toggleQuestion(index)}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        ))}

        {/* Card de contacto de suporte */}
        <div className="mt-4 p-5 border border-white/10 bg-[#12142B]">
          <div className="flex items-center gap-2 font-mono text-xs text-slate-400 mb-2">
            <Terminal size={12} className="text-[#52C47C]" />
            <span className="uppercase tracking-widest text-[#52C47C]">Canal Direto</span>
          </div>
          <p className="text-slate-400 text-xs leading-relaxed mb-4 font-light">
            Tem dúvidas sobre o funcionamento ou implementação na sua empresa?
          </p>
          <a
            href="mailto:suporte@meupontodevenda.com"
            className="block w-full py-2.5 border border-[#52C47C]/40 text-[#52C47C] text-center font-mono text-xs uppercase tracking-widest hover:bg-[#52C47C] hover:text-[#0B0C1E] transition-all flex items-center justify-center gap-2"
          >
            <MessageCircle size={14} />
            Contactar Suporte
          </a>
        </div>
      </div>

      {/* Respostas Desktop */}
      <div className="hidden lg:block flex-1 min-h-[400px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="space-y-2"
          >
            {activeCategory?.questions.map((item, index) => (
              <AccordionItem
                key={index}
                item={item}
                isOpen={openQuestion === index}
                onClick={() => toggleQuestion(index)}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FAQSection;